import { format, isFuture } from 'date-fns'

import { TimeTrackingTab, MY_TEAM_CONSTANTS } from '../../const'
import {
  MyTeamEmployeeData,
  NavigationProps,
  DisplayFormat,
  WeekRange,
} from '../../types'
import { roundHours, roundDays } from '../../utils/format'

interface MyTeamTableRowProps extends NavigationProps {
  employee: MyTeamEmployeeData
  daysInMonth: Date[]
  displayFormat: DisplayFormat
  monthRange: WeekRange
  currentLeaderId: string
}

export const MyTeamTableRow = ({
  employee,
  daysInMonth,
  navigateToTab,
  navigateToResource,
  displayFormat,
  monthRange,
  currentLeaderId,
}: MyTeamTableRowProps) => {
  const trackedDays = roundDays(
    employee.totalHours,
    MY_TEAM_CONSTANTS.HOURS_PER_DAY,
  )
  const trackedHours = roundHours(employee.totalHours)
  const billableDays = roundDays(
    employee.billableHours,
    MY_TEAM_CONSTANTS.HOURS_PER_DAY,
  )
  const billableHours = roundHours(employee.billableHours)
  const nonBillableDays = roundDays(
    employee.nonBillableHours,
    MY_TEAM_CONSTANTS.HOURS_PER_DAY,
  )
  const nonBillableHours = roundHours(employee.nonBillableHours)
  const supportDays = roundDays(
    employee.supportHours,
    MY_TEAM_CONSTANTS.HOURS_PER_DAY,
  )
  const supportHoursVal = roundHours(employee.supportHours)
  const managedTeamDays = roundDays(
    employee.managedTeamHours,
    MY_TEAM_CONSTANTS.HOURS_PER_DAY,
  )
  const managedTeamHoursVal = roundHours(employee.managedTeamHours)
  const notTrackedDays = roundDays(
    employee.notTrackedHours,
    MY_TEAM_CONSTANTS.HOURS_PER_DAY,
  )
  const notTrackedHours = roundHours(employee.notTrackedHours)

  const handleNavigateAllTimes = (
    hours: number,
    extraFilters?: { billable?: boolean; unbilled?: boolean },
  ) =>
    hours > 0
      ? () =>
          navigateToTab(TimeTrackingTab.ALL_TIMES, {
            team: employee.userId,
            teamLeaderId: currentLeaderId,
            isTeamLeader: false,
            dateRange: monthRange,
            ...extraFilters,
          })
      : undefined

  return (
    <tr>
      <td
        className="my-team-member-cell my-team-clickable"
        onClick={() =>
          navigateToResource(employee.userId, 'person-details', 'person_id')
        }
      >
        {employee.userName}
      </td>
      <td
        className={`my-team-metric-cell${trackedHours > 0 ? ' my-team-clickable' : ''}`}
        onClick={handleNavigateAllTimes(trackedHours)}
      >
        {displayFormat === 'days' ? `${trackedDays}d` : `${trackedHours}h`}
      </td>
      <td
        className={`my-team-metric-cell${billableHours > 0 ? ' my-team-clickable' : ''}`}
        onClick={handleNavigateAllTimes(billableHours, { billable: true })}
      >
        {displayFormat === 'days' ? `${billableDays}d` : `${billableHours}h`}
      </td>
      <td
        className={`my-team-metric-cell${nonBillableHours > 0 ? ' my-team-clickable' : ''}`}
        onClick={handleNavigateAllTimes(nonBillableHours, { unbilled: true })}
      >
        {displayFormat === 'days'
          ? `${nonBillableDays}d`
          : `${nonBillableHours}h`}
      </td>
      <td
        className={`my-team-metric-cell${supportHoursVal > 0 ? ' my-team-clickable' : ''}`}
        onClick={handleNavigateAllTimes(supportHoursVal)}
      >
        {displayFormat === 'days' ? `${supportDays}d` : `${supportHoursVal}h`}
      </td>
      <td
        className={`my-team-metric-cell${managedTeamHoursVal > 0 ? ' my-team-clickable' : ''}`}
        onClick={handleNavigateAllTimes(managedTeamHoursVal)}
      >
        {displayFormat === 'days'
          ? `${managedTeamDays}d`
          : `${managedTeamHoursVal}h`}
      </td>
      <td className="my-team-metric-cell">
        {displayFormat === 'days'
          ? `${notTrackedDays}d`
          : `${notTrackedHours}h`}
      </td>
      <td className="my-team-utilization-cell">{employee.utilization}%</td>
      {daysInMonth.map((day) => {
        const dateKey = format(day, 'yyyy-MM-dd')
        const hours = employee.dailyHours.get(dateKey) || 0
        const isWeekend = day.getDay() === 0 || day.getDay() === 6
        const isAbsent = employee.dailyAbsent.get(dateKey)
        const dayBillable = employee.dailyBillableHours.get(dateKey) || 0
        const dayNonBillable = employee.dailyNonBillableHours.get(dateKey) || 0
        const future = isFuture(day)

        const cellClass = [
          'my-team-day-cell',
          isWeekend && 'my-team-day-cell--weekend',
          isAbsent && 'my-team-day-cell--absent',
          dayNonBillable > 0 && 'my-team-day-cell--non-billable',
        ]
          .filter(Boolean)
          .join(' ')

        const tooltipProps =
          hours > 0
            ? {
                'data-tooltip-id': 'my-team-cell-tooltip',
                'data-tooltip-billable': String(dayBillable),
                'data-tooltip-non-billable': String(dayNonBillable),
              }
            : {}

        const displayValue =
          hours > 0 ? hours : future || isAbsent || isWeekend ? '' : '-'

        return (
          <td
            key={dateKey}
            className={`${cellClass}${hours > 0 ? ' my-team-clickable' : ''}`}
            onClick={
              hours > 0
                ? () =>
                    navigateToTab(TimeTrackingTab.ALL_TIMES, {
                      team: employee.userId,
                      teamLeaderId: currentLeaderId,
                      isTeamLeader: false,
                      filterByDate: dateKey,
                    })
                : undefined
            }
            {...tooltipProps}
          >
            {displayValue}
          </td>
        )
      })}
    </tr>
  )
}
